import { useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { buildPeriodOptions, getPreliminaryInfo } from "../lib/periods";
import { fmtDateTimeShort } from "../lib/format";
import type { Period } from "../lib/types";

const PERIODS: Period[] = ["month", "quarter", "half", "year"];
const MONTHS_EXPECTED: Record<Period, number> = { month: 1, quarter: 3, half: 6, year: 12 };

interface Props { availableMonths: string[] }

export function PeriodSelector({ availableMonths }: Props) {
  const { period, periodId, setPeriod, setPeriodId, t, monthStatuses } = useAuth();
  const options = useMemo(() => buildPeriodOptions(period, availableMonths), [period, availableMonths]);

  useEffect(() => {
    if (options.length === 0) return;
    if (!options.find(o => o.id === periodId)) setPeriodId(options[0]!.id);
  }, [options, periodId, setPeriodId]);

  let coverage: string | null = null;
  if (period !== "month" && periodId) {
    const expected = MONTHS_EXPECTED[period];
    let actual = 0;
    for (const m of availableMonths) {
      const opt = buildPeriodOptions(period, [m])[0];
      if (opt && opt.id === periodId) actual++;
    }
    if (actual === expected) coverage = t("coverage_all", { expected });
    else if (actual > 0) coverage = t("coverage_partial", { actual, expected });
  }

  const prelim = useMemo(() => getPreliminaryInfo(period, periodId, monthStatuses), [period, periodId, monthStatuses]);
  let prelimLine: string | null = null;
  if (prelim) {
    const at = prelim.updatedAt ? fmtDateTimeShort(prelim.updatedAt) : "—";
    prelimLine = period === "month"
      ? t("prelim_updated", { at })
      : t("prelim_period", { months: prelim.months.map(m => m.slice(5)).join(", "), at });
  }

  const PERIOD_LABELS: Record<Period, string> = {
    month: t("period_month"),
    quarter: t("period_quarter"),
    half: t("period_half"),
    year: t("period_year"),
  };

  return (
    <div className="px-5 py-3 bg-white border-b border-slate-200 flex-shrink-0">
      <div className="flex bg-slate-100 rounded-lg p-1 mb-2">
        {PERIODS.map(p => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`flex-1 py-1.5 text-xs font-medium rounded-md transition ${period === p ? "bg-indigo-600 text-white" : "text-slate-600"}`}
          >{PERIOD_LABELS[p]}</button>
        ))}
      </div>
      <select
        value={periodId}
        onChange={(e) => setPeriodId(e.target.value)}
        className="w-full bg-slate-50 text-slate-900 rounded-md px-3 py-2 text-sm border border-slate-200"
      >
        {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      {coverage && (
        <div className={`text-[10px] mt-1.5 ${coverage.includes("все") || coverage.includes("всі") ? "text-slate-500" : "text-amber-700"}`}>
          {coverage}
        </div>
      )}
      {prelimLine && (
        <div className="text-[10px] mt-1.5 text-amber-700">⏳ {prelimLine}</div>
      )}
    </div>
  );
}
